import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { alertActions } from "../redux/AlertController";
import useLogout from "./useLogout";

const POLL_INTERVAL_MS = 1500;

export const useNotifications = () => {
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const [history, setHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);

    const [pendingRejectionCount, setPendingRejectionCount] = useState(0);
    const [isSendingRejectionBatch, setIsSendingRejectionBatch] = useState(false);
    const [resendingHistoryId, setResendingHistoryId] = useState(null);

    const dispatch = useDispatch();
    const user = useSelector(state => state.userController.user);
    const navigate = useNavigate();
    const { logout } = useLogout();
    const pollTimeoutRef = useRef(null);

    const fetchRecipients = async (filters) => {
        if (!user) {
            navigate('/login');
            return;
        }
        setIsLoadingRecipients(true);
        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach((v) => params.append(key, v));
                } else if (value !== '' && value !== null && value !== undefined) {
                    params.append(key, value);
                }
            });

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/notifications/recipients?${params}`, {
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                setPhoneNumbers(json.phoneNumbers);
                setHasSearched(true);
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsLoadingRecipients(false);
    };

    const resetRecipients = () => {
        setPhoneNumbers([]);
        setHasSearched(false);
    };

    const sendNotification = async (message, filters) => {
        setIsSending(true);
        let success = false;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/notifications/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user?.token}`
                },
                body: JSON.stringify({ phoneNumbers, message, filters })
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'success' }));
                success = true;
                fetchHistory();
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsSending(false);
        return success;
    };

    // silent=true skips the full-page spinner and error toasts — used for the
    // background polling that keeps in-progress sends' "sent / total" live.
    const fetchHistory = async (silent = false) => {
        if (!user) return;
        if (!silent) setIsLoadingHistory(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/notifications/history`, {
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                if (!silent) dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                setHistory(json.history);

                clearTimeout(pollTimeoutRef.current);
                const stillInProgress = json.history.some((item) => item.sendProgress && !item.sendProgress.isComplete);
                if (stillInProgress) {
                    pollTimeoutRef.current = setTimeout(() => fetchHistory(true), POLL_INTERVAL_MS);
                }
            }
        } catch (err) {
            if (!silent) dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        if (!silent) setIsLoadingHistory(false);
    };

    const fetchPendingRejectionCount = async () => {
        if (!user) return;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/notifications/pending-rejections`, {
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await res.json();
            if (res.ok) {
                setPendingRejectionCount(json.count);
            }
        } catch (err) {
            // Non-critical — the banner just won't show a count
        }
    };

    const sendRejectionBatch = async () => {
        setIsSendingRejectionBatch(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/notifications/send-rejection-batch`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'success' }));
                fetchPendingRejectionCount();
                fetchHistory();
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsSendingRejectionBatch(false);
    };

    const resendFailed = async (historyId) => {
        setResendingHistoryId(historyId);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/notifications/history/${historyId}/resend-failed`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'success' }));
                fetchPendingRejectionCount();
                fetchHistory();
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setResendingHistoryId(null);
    };

    useEffect(() => {
        if (!user) {
            navigate('/login');
        } else {
            fetchHistory();
            fetchPendingRejectionCount();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        return () => clearTimeout(pollTimeoutRef.current);
    }, []);

    return {
        phoneNumbers,
        isLoadingRecipients,
        isSending,
        hasSearched,
        fetchRecipients,
        resetRecipients,
        sendNotification,
        history,
        isLoadingHistory,
        pendingRejectionCount,
        isSendingRejectionBatch,
        sendRejectionBatch,
        resendingHistoryId,
        resendFailed
    };
};
